/**
 * DynamicSpecificationCollection Value Object
 * 
 * Collection of specifications with intelligent sorting and table layout logic.
 * Handles dynamic specification rendering with priority-based ordering.
 */

import {
  DynamicSpecification,
  type DatabaseSpecificationRecord,
  type SpecificationTemplate,
} from './DynamicSpecification'
import { isSlingCategory } from '@/utils/categoryFamily'
import { SmartSpecificationFormatter } from '../services/SmartSpecificationFormatter'

/**
 * Collection of specifications with sorting and layout capabilities
 */
export class DynamicSpecificationCollection {
  private readonly specs: DynamicSpecification[]
  private readonly formatter: SmartSpecificationFormatter

  constructor(specs: DynamicSpecification[], formatter?: SmartSpecificationFormatter) {
    // Only keep specs with values
    this.specs = specs.filter(s => s.hasValue())
    this.formatter = formatter || new SmartSpecificationFormatter()
  }

  /**
   * Create collection from database results
   * Automatically filters out specs without values
   */
  static fromDatabaseResults(
    dbSpecs: DatabaseSpecificationRecord[],
    templates?: Map<string, SpecificationTemplate>,
    formatter?: SmartSpecificationFormatter
  ): DynamicSpecificationCollection {
    const specifications = dbSpecs
      .map(dbSpec => {
        const template = templates?.get(dbSpec.spec_key)
        return DynamicSpecification.fromDatabase(dbSpec, template)
      })
      .filter((spec): spec is DynamicSpecification => spec !== null)

    return new DynamicSpecificationCollection(specifications, formatter)
  }

  /**
   * Get specifications sorted by display priority
   * Priority order:
   * 1. Database display_order (if set)
   * 2. Importance score (critical specs first)
   * 3. Alphabetical by label
   */
  getSortedForDisplay(): DynamicSpecification[] {
    const sorted = [...this.specs].sort((a, b) => {
      // Priority from database template takes precedence
      if (a.priority !== undefined && b.priority !== undefined) {
        if (a.priority !== b.priority) {
          return a.priority - b.priority
        }
      }

      // One has priority, other doesn't - prioritized one goes first
      if (a.priority !== undefined && b.priority === undefined) {
        return -1
      }
      if (a.priority === undefined && b.priority !== undefined) {
        return 1
      }

      // Fallback to importance scoring
      const aImportance = a.getImportanceScore()
      const bImportance = b.getImportanceScore()

      if (aImportance !== bImportance) {
        return aImportance - bImportance
      }

      // Alphabetical as final tiebreaker
      return a.label.localeCompare(b.label)
    })

    return sorted
  }

  /**
   * Convert specifications to table display rows
   * 
   * @param columns Number of columns in the table (default: 3)
   * @returns Array of rows, each row containing formatted cells
   */
  toDisplayRows(columns: number = 3): string[][] {
    const sorted = this.getSortedForDisplay()
    
    // Format each specification
    const formatted = sorted.map(spec => {
      const value = this.formatter.format(spec)
      return {
        label: spec.label,
        value: value,
      }
    })

    // Pad to multiple of columns for consistent layout
    while (formatted.length % columns !== 0) {
      formatted.push({ label: '', value: '' })
    }

    // Create rows with combined label/value cells
    const rows: string[][] = []
    for (let i = 0; i < formatted.length; i += columns) {
      const row: string[] = []
      
      for (let j = 0; j < columns; j++) {
        const item = formatted[i + j]
        if (item.label && item.value && item.label.trim() !== '' && item.value.trim() !== '') {
          // Combine label and value with line break
          row.push(`${item.label}\n${item.value}`)
        } else {
          // Empty cell for padding
          row.push('')
        }
      }
      
      rows.push(row)
    }

    return rows
  }

  /**
   * Get specification by key
   */
  findByKey(key: string): DynamicSpecification | null {
    const lowerKey = key.toLowerCase()
    return this.specs.find(s => s.key.toLowerCase() === lowerKey) || null
  }

  /**
   * Get specifications matching a pattern
   */
  findByPattern(pattern: string | RegExp): DynamicSpecification[] {
    const regex = typeof pattern === 'string' ? new RegExp(pattern, 'i') : pattern
    return this.specs.filter(s => regex.test(s.key) || regex.test(s.label))
  }

  /**
   * Get all specifications
   */
  getAll(): DynamicSpecification[] {
    return [...this.specs]
  }

  /**
   * Get formatted values as a map
   */
  getFormattedValues(): Map<string, string> {
    const formatted = new Map<string, string>()
    
    for (const spec of this.specs) {
      formatted.set(spec.key, this.formatter.format(spec))
    }
    
    return formatted
  }

  /**
   * Get specification count
   */
  count(): number {
    return this.specs.length
  }

  /**
   * Check if collection is empty
   */
  isEmpty(): boolean {
    return this.specs.length === 0
  }

  /**
   * Check if collection has a specific key
   */
  hasKey(key: string): boolean {
    return this.findByKey(key) !== null
  }

  /**
   * Overlay NDT date specs with a new ISO date (48-month renewal).
   * Updates existing ndt_date / last_ndt_date values, or adds ndt_date when
   * the equipment category is NDT-eligible and no date spec exists yet.
   */
  withNdtDate(isoDate: string, categoryCode?: string | null): DynamicSpecificationCollection {
    const ndtKeys = new Set(['ndt_date', 'last_ndt_date'])
    let replaced = false
    const updated = this.specs.map(spec => {
      if (ndtKeys.has(spec.key.toLowerCase())) {
        replaced = true
        return spec.withValue(isoDate)
      }
      return spec
    })

    if (!replaced) {
      const code = (categoryCode || '').toUpperCase()
      const eligible =
        isSlingCategory(code) || code.startsWith('F')
      if (eligible) {
        const created = DynamicSpecification.fromDatabase({
          spec_key: 'ndt_date',
          value_date: isoDate,
          template: {
            label_no: 'Siste NDT',
            label_en: 'Last NDT',
            data_type: 'date',
          },
        })
        if (created) updated.push(created)
      }
    }

    return new DynamicSpecificationCollection(updated, this.formatter)
  }

  /**
   * Filter specifications by predicate
   */
  filter(predicate: (spec: DynamicSpecification) => boolean): DynamicSpecificationCollection {
    const filtered = this.specs.filter(predicate)
    return new DynamicSpecificationCollection(filtered, this.formatter)
  }

  /**
   * Map specifications to another type
   */
  map<T>(mapper: (spec: DynamicSpecification) => T): T[] {
    return this.specs.map(mapper)
  }

  /**
   * Get debug information about the collection
   */
  debug(): {
    count: number
    keys: string[]
    sorted: Array<{ key: string; label: string; priority?: number; importance: number }>
  } {
    const sorted = this.getSortedForDisplay()
    
    return {
      count: this.count(),
      keys: this.specs.map(s => s.key),
      sorted: sorted.map(s => ({
        key: s.key,
        label: s.label,
        priority: s.priority,
        importance: s.getImportanceScore(),
      })),
    }
  }

  /**
   * Create a string representation for debugging
   */
  toString(): string {
    return `DynamicSpecificationCollection(count=${this.count()}, keys=[${this.specs.map(s => s.key).join(', ')}])`
  }
}
